import React, { useEffect, useRef, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import "./DemoModal.css";

interface DemoModalProps {
  isOpen: boolean;
  onClose: () => void;
  isLocalhost: boolean;
}

const STORYLANE_URL = "https://app.storylane.io/share/pakottugzdnb";

const DemoModal: React.FC<DemoModalProps> = ({
  isOpen,
  onClose,
  isLocalhost,
}) => {
  const { t } = useTranslation("marketing");
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const [iframeError, setIframeError] = useState(false);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
        return;
      }

      if (e.key === "Tab" && modalRef.current) {
        const focusableElements = modalRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );

        if (focusableElements.length === 0) return;

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (e.shiftKey && document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        } else if (!e.shiftKey && document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    },
    [onClose]
  );

  useEffect(() => {
    if (isOpen) {
      previousFocusRef.current = document.activeElement as HTMLElement;
      document.body.style.overflow = "hidden";
      document.addEventListener("keydown", handleKeyDown);

      // Focus close button after render
      requestAnimationFrame(() => {
        closeButtonRef.current?.focus();
      });
    } else {
      document.body.style.overflow = "";
      document.removeEventListener("keydown", handleKeyDown);

      // Return focus to triggering element
      previousFocusRef.current?.focus();
      previousFocusRef.current = null;
      setIframeError(false);
    }

    return () => {
      document.body.style.overflow = "";
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className="demo-modal__overlay"
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="demo-modal-title"
      ref={modalRef}
    >
      <div className="demo-modal__content">
        <div className="demo-modal__header">
          <h2 id="demo-modal-title" className="demo-modal__title">
            {t("landing.demoPreview.modalTitle")}
          </h2>
          <button
            ref={closeButtonRef}
            className="demo-modal__close"
            onClick={onClose}
            aria-label={t("landing.demoPreview.modalClose")}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                d="M18 6L6 18M6 6l12 12"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
        <div className="demo-modal__body">
          {isLocalhost ? (
            <div className="demo-modal__placeholder">
              <p>{t("landing.demoPreview.localhostPlaceholder")}</p>
            </div>
          ) : iframeError ? (
            <div className="demo-modal__fallback">
              <p>{t("landing.demoPreview.unavailable", "Demo unavailable. Please try again later.")}</p>
            </div>
          ) : (
            <iframe
              className="demo-modal__iframe"
              src={STORYLANE_URL}
              title={t("landing.demoPreview.modalTitle")}
              allow="fullscreen"
              onError={() => setIframeError(true)}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default DemoModal;
